import React, { useEffect, useRef, useState } from "react";
import { Card, Col, Row, Image, Stack } from "react-bootstrap";
import { User } from "../../../../types/user";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBan, faInfoCircle, faUserShield, faBirthdayCake } from "@fortawesome/free-solid-svg-icons";
import { Tooltip } from "react-tooltip";

interface HouseholdCardState {
  user: User;
  setEditGuardian: any;
  setEditChild: any;
  setEditHousehold: any;
  setCreate: any;
  currentUser: User | undefined;
  setCurrentUser: any;
  userToSave: boolean;
  setShowModal: any;
  setIsLoading: any;
}

const HouseholdCard = ({
  user,
  setEditGuardian,
  setEditChild,
  setEditHousehold,
  setCreate,
  currentUser,
  setCurrentUser,
  userToSave,
  setShowModal,
  setIsLoading,
}: HouseholdCardState) => {
  const [age, setAge] = useState<number | null>(15);
  const [numberGroups, setNumberGroups] = useState<number>(0);
  const [groups, setGroups] = useState<string[]>([]);
  const [banDateExpired, setBanDateExpired] = useState<boolean>(false);
  const [showGroupIcon, setShowGroupIcon] = useState<boolean>(false);
  const [profileImage, setProfileImage] = useState<string>("");
  const [isBirthday, setIsBirthday] = useState<boolean>(false);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [showExplosion, setShowExplosion] = useState<boolean>(false);
  const [pieces, setPieces] = useState<Array<any>>([]);
  const triggeredRef = useRef<boolean>(false);
  const timeoutRef = useRef<number | null>(null);
  const [exploded, setExploded] = useState<boolean>(false);
  const [fadingInline, setFadingInline] = useState<boolean>(false);
  const fadeTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    setProfileImage("");
    user?.profile?.map((p: any) => {
      const base64String = "data:image/jpeg;base64, " + p?.data;
      setProfileImage(base64String);
    });

    // Check if birthdate is valid
    if (user?.birthdate && user?.birthdate !== "0000-00-00") {
      const calculateAge = (birthdate: string) => {
        const birthDateObj = new Date(birthdate);
        const today = new Date();

        let age = today.getFullYear() - birthDateObj.getFullYear();
        const monthDiff = today.getMonth() - birthDateObj.getMonth();

        // Adjust age if the birthdate hasn't occurred yet this year
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDateObj.getDate())) {
          age--;
        }
        return age;
      };

      setAge(calculateAge(user.birthdate));
    } else {
      setAge(null); // Don't calculate age if birthdate is invalid
    }

    if (user.groups) {
      setNumberGroups(user?.groups?.length);
      const firstTwo = user.groups.slice(0, 2);
      setGroups(firstTwo);
      if (user?.groups?.length > 2) {
        setShowGroupIcon(true);
      }
    }

    //check ban expiration date
    if (user.banned_expiration_at) {
      const expireDate = new Date(user?.banned_expiration_at);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      setBanDateExpired(expireDate <= today);
    }
    // check if today is the user's birthday (month and day match)
    if (user.birthdate && user.birthdate !== "0000-00-00") {
      const parts = (user.birthdate || "").split("-").map((p: any) => parseInt(p, 10));
      if (parts.length >= 3 && !parts.some((n: number) => Number.isNaN(n))) {
        const [y, m, d] = parts;
        const bd = new Date(y, m - 1, d); // construct in local TZ to avoid UTC parse shifts
        const today = new Date();
        if (bd.getMonth() === today.getMonth() && bd.getDate() === today.getDate()) {
          setIsBirthday(true);
        } else {
          setIsBirthday(false);
        }
      } else {
        setIsBirthday(false);
      }
    } else {
      setIsBirthday(false);
    }

    setIsLoading(false);
  }, [user]);

  // trigger explosion on demand (hover). Prevent rapid retriggers
  const triggerExplosion = () => {
    if (!isBirthday) return;
    if (triggeredRef.current) return;
    // start fading inline confetti and mark exploded after fade
    setFadingInline(true);
    if (fadeTimeoutRef.current) window.clearTimeout(fadeTimeoutRef.current);
    fadeTimeoutRef.current = window.setTimeout(() => {
      setExploded(true);
      fadeTimeoutRef.current = null;
    }, 500);

    triggeredRef.current = true;

    const originX = Math.round(window.innerWidth / 2);
    const originY = Math.round(Math.min(120, window.innerHeight * 0.15));

    const count = 28;
    const newPieces = Array.from({ length: count }).map(() => {
      const angle = Math.random() * Math.PI * 2;
      const dist = 80 + Math.random() * 260; // pixels
      const tx = Math.round(Math.cos(angle) * dist) + "px";
      const ty = Math.round(Math.sin(angle) * dist) + "px";
      const delay = (Math.random() * 0.35).toFixed(2) + "s";
      const color = Math.floor(Math.random() * 6);
      return { x: originX + "px", y: originY + "px", tx, ty, delay, color };
    });

    setPieces(newPieces);
    setShowExplosion(true);

    if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    timeoutRef.current = window.setTimeout(() => {
      setShowExplosion(false);
      triggeredRef.current = false;
      timeoutRef.current = null;
    }, 1400);
  };

  // cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
      if (fadeTimeoutRef.current) window.clearTimeout(fadeTimeoutRef.current);
    };
  }, []);

  const handleOnClick = () => {
    if (userToSave) {
      setShowModal(true);
    } else {
      if (currentUser?.id === user?.id && currentUser?.relationship === user?.relationship) {
        setCurrentUser(undefined);
      } else {
        setEditGuardian(false);
        setEditChild(false);
        setEditHousehold(false);
        setCurrentUser(undefined);
        setTimeout(() => {
          setCurrentUser(user);
        }, 0);
      }

      setTimeout(() => {
        if (currentUser?.id === user?.id && currentUser?.relationship === user?.relationship) {
          setEditGuardian(false);
          setEditChild(false);
          setEditHousehold(false);
        } else if (user.role === "family") {
          setEditGuardian(true);
          setEditChild(false);
          setEditHousehold(false);
        } else if (user.relationship === "child") {
          setEditGuardian(false);
          setEditChild(true);
          setEditHousehold(false);
        } else {
          setEditGuardian(false);
          setEditChild(false);
          setEditHousehold(true);
        }

        setCreate(false);
        setShowModal(false);
      }, 0);
    }
  };

  const handleViewAllGroups = (e: any) => {
    e.stopPropagation();

    if (user.groups) {
      setGroups(user.groups);
      setShowGroupIcon(false);
    }
  };

  return (
    <>
      <Card
        ref={cardRef as any}
        className={user?.is_banned ? "bg-light card-household card-danger w-100 h-100" : "bg-light card-household w-100 h-100"}
        onClick={handleOnClick}
      >
        <Card.Body className="d-flex flex-column">
          <Row className="me-0">
            <Col className="col-12">
              <h5
                className={
                  currentUser?.id === user?.id && currentUser?.relationship === user?.relationship
                    ? "app-orange-text pb-0 mb-0"
                    : "app-header-text pb-0 mb-0"
                }
              >
                {user.firstname} {user.preferredname ? "'" + user.preferredname + "'" : ""} {user.lastname}
              </h5>
            </Col>
          </Row>
          <Row>
            <Col className="col-12 text-end">
              {user?.is_banned === 1 && (
                <div
                  className="position-absolute top-0 end-0"
                  data-tooltip-id="tooltip-ban"
                  data-tooltip-content={
                    banDateExpired
                      ? `Ban Expired`
                      : `Banned ${user?.banned_expiration_at ? "Until " + user?.banned_expiration_at : "Indefinitely"}`
                  }
                >
                  <Stack direction="horizontal">
                    <FontAwesomeIcon icon={faBan} className="app-danger-text me-2 mt-2" />
                  </Stack>
                  <Tooltip id="tooltip-ban" place="top" style={{ padding: "5px 10px", zIndex: "100" }} />
                </div>
              )}
              {isBirthday && user?.is_banned !== 1 && exploded && (
                <span
                  className="position-absolute top-0 end-0"
                  data-tooltip-id={`tooltip-bday-${user.id}`}
                  data-tooltip-content={`Birthday Today!`}
                  role="button"
                  tabIndex={0}
                  aria-label="Celebrate Birthday"
                  onMouseEnter={() => triggerExplosion()}
                  onFocus={() => triggerExplosion()}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      triggerExplosion();
                    }
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <FontAwesomeIcon icon={faBirthdayCake} className="app-primary-color-inverse me-2 mt-2" />
                  <Tooltip id={`tooltip-bday-${user.id}`} place="top" style={{ padding: "5px 10px", zIndex: "100" }} />
                </span>
              )}
            </Col>
          </Row>
          <Row>
            <Col className={"col-8"}>
              {user.relationship === "child" ? (
                <Row className="mb-1">
                  <p className="text-muted first-letter mb-0">
                    {user?.grade && age
                      ? `Grade ${user?.grade} - Age ${age}`
                      : user?.grade
                      ? `Grade ${user?.grade}`
                      : age
                      ? `Age ${age}`
                      : ""}
                  </p>
                </Row>
              ) : (
                <Row className="mb-1">
                  <p className="text-muted first-letter mb-0">{user.relationship}</p>
                </Row>
              )}
              <Row>
                <p className="pt-0 pb-0 mb-0">
                  <span className="font-size-dynamic-smaller">{user.email}</span>
                </p>
                <p className="pt-0 pb-0 mb-0">
                  <span className="font-size-dynamic-smaller">
                    {user.phonenumber === "1"
                      ? ""
                      : user.phonenumber
                      ? user.phonenumber.replace(/(\d{1})(\d{3})(\d{3})(\d{3})/, "+$1 ($2) $3-$4")
                      : ""}
                  </span>
                </p>
                <Row>
                  <hr className="ms-2 mt-2 mb-2" />
                </Row>
                {numberGroups > 0 &&
                  groups.map((g: any, index: number) => (
                    <p key={index} className="pt-0 pb-0 mb-0 text-muted">
                      <span className="font-size-dynamic-larger">{g.name}</span>
                      {index === 1 && showGroupIcon && (
                        <FontAwesomeIcon icon={faInfoCircle} className="ms-2 z-index-custom" onClick={(e: any) => handleViewAllGroups(e)} />
                      )}
                    </p>
                  ))}
                {isBirthday && user?.is_banned !== 1 && !exploded && (
                  <div
                    className={`position-absolute confetti-inline-container ${fadingInline ? "fade-confetti" : ""}`}
                    style={{ top: "1px", left: "48.5%", transform: "translateX(-50%)", width: "90%" }}
                    aria-hidden={showExplosion}
                    onMouseEnter={() => triggerExplosion()}
                    onFocus={() => triggerExplosion()}
                  >
                    {Array.from({ length: 12 }).map((_, i) => (
                      <span
                        key={i}
                        className={`confetti-inline-piece confetti-inline-${i % 6}`}
                        style={{ left: `${(i * (100 / (12 - 1))).toFixed(2)}%`, animationDelay: `${(i % 5) * 0.12}s` }}
                      />
                    ))}
                  </div>
                )}
              </Row>
            </Col>
            <Col className="col-4">
              {profileImage ? <Image src={profileImage} thumbnail /> : <Image src="/blank-profile-picture.jpg" thumbnail />}
            </Col>
          </Row>
          {user.is_guardian === 1 && (
            <Row>
              <Col className="col-12 text-end">
                {user.is_primary ? (
                  <div className="position-absolute bottom-0 end-0">
                    <Stack direction="horizontal">
                      <small className="small-text app-primary-color-inverse me-1 mb-2 bg-light">Main Contact</small>
                      <FontAwesomeIcon icon={faUserShield} className="app-primary-color-inverse me-2 mb-2" />
                    </Stack>
                  </div>
                ) : (
                  <div className="position-absolute bottom-0 end-0">
                    <Stack direction="horizontal">
                      <text className="p-0 m-0 me-2 app-primary-color-inverse">
                        <small> </small>
                      </text>
                      <FontAwesomeIcon icon={faUserShield} className="app-primary-color-inverse me-2 mb-2" />
                    </Stack>
                  </div>
                )}
              </Col>
            </Row>
          )}
        </Card.Body>
      </Card>
      {showExplosion && (
        <div className="confetti-explosion" aria-hidden>
          {pieces.map((p: any, i: number) => (
            <span
              key={i}
              className={`confetti-piece confetti-${p.color}`}
              style={{
                ["--x" as any]: p.x,
                ["--y" as any]: p.y,
                ["--tx" as any]: p.tx,
                ["--ty" as any]: p.ty,
                animationDelay: p.delay,
              }}
            />
          ))}
        </div>
      )}
    </>
  );
};

export default HouseholdCard;
